<script setup>
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: the FLOW-button part of a
// template send (campaign step 2, direct send, new conversation). Resolves
// the button's flow in the Rumo library, shows its status, lets the user
// prefill the opening screen (flow_action_data, Liquid allowed) and blocks
// sending when the flow is deprecated or blocked. Emits the value the caller
// passes to applyFlowButtonParam: {flowActionData, kaptionFlowId, blocked}.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import KaptionWhatsappFlowsAPI from 'dashboard/api/kaptionWhatsappFlows';
import {
  findFlowForButton,
  getFlowButton,
  isFlowBlockedForSending,
  screenDataKeys,
} from 'dashboard/helper/kaptionWhatsappFlows';

const props = defineProps({
  inboxId: { type: [Number, String], default: null },
  template: { type: Object, default: null },
  modelValue: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const flow = ref(null);
const flowJson = ref(null);
const lookupDone = ref(false);
const prefill = ref({ ...(props.modelValue?.flowActionData || {}) });

const flowButton = computed(() => getFlowButton(props.template));
const isBlocked = computed(() => isFlowBlockedForSending(flow.value?.status));

const prefillKeys = computed(() =>
  screenDataKeys(flowJson.value, flowButton.value?.button?.navigate_screen)
);

const statusLabel = computed(() =>
  flow.value?.status
    ? t(`KAPTION_WHATSAPP_FLOWS.STATUS.${flow.value.status}`)
    : ''
);

const prefillHint = computed(() =>
  t('KAPTION_WHATSAPP_FLOWS.TEMPLATE.PREFILL_HINT', {
    variable: '{{contact.name}}',
  })
);

const flowLine = computed(() =>
  flow.value
    ? `${flow.value.name} · ${t('KAPTION_WHATSAPP_FLOWS.TEMPLATE.STATUS_LABEL', { status: statusLabel.value })}`
    : ''
);

const publish = () => {
  emit('update:modelValue', {
    flowActionData: { ...prefill.value },
    kaptionFlowId: flow.value?.id || null,
    blocked: isBlocked.value,
  });
};

const resolveFlow = async () => {
  flow.value = null;
  flowJson.value = null;
  lookupDone.value = false;
  if (!props.inboxId || !flowButton.value) return;
  try {
    const { data } = await KaptionWhatsappFlowsAPI.list(props.inboxId);
    flow.value = findFlowForButton(data?.payload || [], flowButton.value.button);
    if (flow.value) {
      const detail = await KaptionWhatsappFlowsAPI.show(
        props.inboxId,
        flow.value.id
      );
      flowJson.value =
        detail.data?.published_flow_json || detail.data?.flow_json || null;
    }
  } catch {
    flow.value = null;
  } finally {
    lookupDone.value = true;
    publish();
  }
};

const updatePrefill = (key, value) => {
  prefill.value = { ...prefill.value, [key]: value };
  publish();
};

watch(
  () => [props.inboxId, props.template?.id, props.template?.name],
  resolveFlow,
  { immediate: true }
);
</script>

<template>
  <section
    v-if="flowButton"
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak"
    data-testid="kaption-flow-template-section"
  >
    <div class="flex flex-wrap items-center gap-2">
      <span class="i-lucide-list-checks size-4 text-n-slate-11" />
      <span class="text-sm font-medium text-n-slate-12">
        {{
          t('KAPTION_WHATSAPP_FLOWS.TEMPLATE.FLOW_BUTTON', {
            text: flowButton.button.text,
          })
        }}
      </span>
      <span v-if="flow" class="text-xs text-n-slate-11">
        {{ flowLine }}
      </span>
    </div>

    <p
      v-if="isBlocked"
      role="alert"
      class="p-3 text-sm rounded-lg bg-n-ruby-2 text-n-ruby-11"
      data-testid="kaption-flow-template-blocked"
    >
      {{ t('KAPTION_WHATSAPP_FLOWS.TEMPLATE.BLOCKED', { status: statusLabel }) }}
    </p>

    <p
      v-else-if="lookupDone && !flow"
      class="text-sm text-n-amber-11"
      data-testid="kaption-flow-template-unknown"
    >
      {{ t('KAPTION_WHATSAPP_FLOWS.TEMPLATE.UNKNOWN_FLOW') }}
    </p>

    <div v-if="!isBlocked && prefillKeys.length" class="flex flex-col gap-2">
      <div class="text-xs font-medium text-n-slate-11">
        {{ t('KAPTION_WHATSAPP_FLOWS.TEMPLATE.PREFILL') }}
      </div>
      <label
        v-for="key in prefillKeys"
        :key="key"
        class="grid grid-cols-[minmax(110px,160px)_1fr] items-center gap-2 text-sm"
      >
        <span class="font-mono text-xs truncate text-n-slate-11">{{ key }}</span>
        <input
          :value="prefill[key] || ''"
          class="h-8 px-2 mb-0 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          :data-testid="`kaption-flow-prefill-${key}`"
          @input="updatePrefill(key, $event.target.value)"
        />
      </label>
      <p class="text-xs text-n-slate-10">
        {{ prefillHint }}
      </p>
    </div>

    <p v-if="!isBlocked" class="text-xs text-n-slate-10">
      {{ t('KAPTION_WHATSAPP_FLOWS.TEMPLATE.TOKEN_NOTE') }}
    </p>
  </section>
</template>
